import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { Container, Row, Col } from "react-bootstrap"
import DataTable from "react-data-table-component"
import Select, { components as selectComponents } from "react-select"
import axios from "axios"
import { saveAs } from "file-saver"
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer"
import {
  FaEnvelope,
  FaSms,
  FaWhatsapp,
  FaPaperclip,
  FaRegClock,
  FaExclamationTriangle,
  FaTimes,
  FaInbox,
  FaSearch,
  FaRedo,
  FaFilter,
  FaFileExcel,
} from "react-icons/fa"

/* ------------------------------------------------------------------ */
/*  Shared visual language (kept identical to the ATS report pages)   */
/* ------------------------------------------------------------------ */
const reportCardStyle = {
  background: "#fff",
  border: "1px solid #d9e4ef",
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
}

const reportTableStyles = {
  table: {
    style: {
      borderLeft: "1px solid #cddcea",
      borderRight: "1px solid #cddcea",
      borderTop: "1px solid #cddcea",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#eaf4ff",
      borderTop: "none",
      borderBottom: "1px solid #c4d8ea",
      minHeight: "42px",
    },
  },
  headCells: {
    style: {
      color: "#0b3a5d",
      fontSize: "11px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
      justifyContent: "center",
      textAlign: "center",
      paddingLeft: "10px",
      paddingRight: "10px",
      overflow: "visible",
      whiteSpace: "normal",
      borderRight: "1px solid #cfe0f1",
    },
  },
  rows: {
    style: {
      minHeight: "50px",
      borderBottom: "1px solid #dbe6f1 !important",
      cursor: "pointer",
    },
    stripedStyle: { backgroundColor: "#f9fbfd" },
    highlightOnHoverStyle: {
      backgroundColor: "#eef6ff",
      transitionDuration: "0.15s",
      transitionProperty: "background-color",
      outline: "none",
    },
  },
  cells: {
    style: {
      color: "#1f2f46",
      fontSize: "12px",
      justifyContent: "center",
      textAlign: "center",
      paddingLeft: "10px",
      paddingRight: "10px",
      borderRight: "1px solid #eaeff5",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #cddcea",
      minHeight: "48px",
    },
  },
}

const paginationComponentOptions = {
  rowsPerPageText: "Rows per page",
  rangeSeparatorText: "of",
  selectAllRowsItem: false,
}

/* Local timezone label, e.g. "GMT+5:30" — shown next to timestamps. */
const TZ_LABEL = (() => {
  try {
    const offset = -new Date().getTimezoneOffset()
    const sign = offset >= 0 ? "+" : "-"
    const abs = Math.abs(offset)
    const hh = Math.floor(abs / 60)
    const mm = abs % 60
    return `GMT${sign}${hh}${mm ? ":" + String(mm).padStart(2, "0") : ""}`
  } catch (e) {
    return "Local"
  }
})()

const formatDateTime = value => {
  if (!value) return "-"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "-"
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const truncate = (text, len = 60) => {
  if (!text) return "-"
  const clean = String(text).replace(/<[^>]*>/g, "").trim()
  return clean.length > len ? clean.slice(0, len).trimEnd() + "…" : clean
}

/* ------------------------------------------------------------------ */
/*  Channel + status configuration                                    */
/* ------------------------------------------------------------------ */
const CHANNELS = {
  email: {
    key: "email",
    label: "Email History",
    short: "Email",
    icon: <FaEnvelope />,
    accent: "#2563eb",
    bg: "#e6efff",
    contactLabel: "Email Address",
    /* Delivered is only shown when the provider confirms it. */
    statusOptions: ["Pending", "Sent", "Delivered", "Failed"],
  },
  sms: {
    key: "sms",
    label: "SMS History",
    short: "SMS",
    icon: <FaSms />,
    accent: "#7c3aed",
    bg: "#efe7fe",
    contactLabel: "Mobile Number",
    statusOptions: ["Pending", "Sent", "Delivered", "Failed"],
  },
  whatsapp: {
    key: "whatsapp",
    label: "WhatsApp History",
    short: "WhatsApp",
    icon: <FaWhatsapp />,
    accent: "#059669",
    bg: "#dcf5ea",
    contactLabel: "Mobile Number",
    /* Read is only shown when the provider confirms a read receipt. */
    statusOptions: ["Pending", "Sent", "Delivered", "Read", "Failed"],
  },
}

const STATUS_STYLE = {
  Pending: { color: "#92600a", bg: "#fef3d7", dot: "#d99e15" },
  Sent: { color: "#1d4ed8", bg: "#e4edff", dot: "#2f6bff" },
  Delivered: { color: "#0f766e", bg: "#d7f2ee", dot: "#0f9d8f" },
  Read: { color: "#047857", bg: "#d3f5e4", dot: "#059669" },
  Failed: { color: "#b42318", bg: "#fde4e1", dot: "#e5473b" },
}

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Pending
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        color: s.color,
        background: s.bg,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: s.dot,
          display: "inline-block",
        }}
      />
      {status}
    </span>
  )
}

const NOTIFICATION_TYPES = [
  "Interview Invitation",
  "Feedback Request",
  "Assessment Notification",
  "Offer Letter",
  "Application Received",
  "Screening Reminder",
  "Document Request",
]

/* ------------------------------------------------------------------ */
/*  Realistic dummy dataset (used when no aggregate API is available)  */
/*  Shape mirrors the backend Email / SMSLog / WhatsappMessageSave     */
/*  models so it can be swapped for a live endpoint with no UI change. */
/* ------------------------------------------------------------------ */
const JOBS = [
  { jobId: 4101, jobTitle: "Senior React Developer" },
  { jobId: 4102, jobTitle: "Backend Engineer (Python/Django)" },
  { jobId: 4103, jobTitle: "QA Automation Lead" },
  { jobId: 4104, jobTitle: "Product Designer (UX)" },
  { jobId: 4105, jobTitle: "DevOps Engineer" },
  { jobId: 4106, jobTitle: "Talent Acquisition Specialist" },
]

const PEOPLE = [
  { name: "Aarav Sharma", email: "aarav.sharma@example.com", mobile: "+91 98765 43210" },
  { name: "Priya Nair", email: "priya.nair@example.com", mobile: "+91 90123 45678" },
  { name: "Rohan Mehta", email: "rohan.mehta@example.com", mobile: "+91 99887 66554" },
  { name: "Sneha Kapoor", email: "sneha.kapoor@example.com", mobile: "+91 98111 22334" },
  { name: "Vikram Iyer", email: "vikram.iyer@example.com", mobile: "+91 97654 11223" },
  { name: "Meera Joshi", email: "meera.joshi@example.com", mobile: "+91 96500 78901" },
  { name: "Karan Malhotra", email: "karan.malhotra@example.com", mobile: "+91 95000 44556" },
  { name: "Ananya Reddy", email: "ananya.reddy@example.com", mobile: "+91 94400 33221" },
  { name: "Dev Patel", email: "dev.patel@example.com", mobile: "+91 93300 55667" },
  { name: "Ishita Bose", email: "ishita.bose@example.com", mobile: "+91 92200 66778" },
]

const SENDERS = ["System", "Neha Gupta", "Arjun Rao", "Sara Thomas", "System", "Rahul Verma"]

const EMAIL_BODY = (person, type, job) =>
  `<p>Dear ${person.name},</p>
   <p>This message relates to your application for the position of <strong>${
     job ? job.jobTitle : "one of our open roles"
   }</strong>${job ? ` (Job ID: ${job.jobId})` : ""}.</p>
   <p>${
     type === "Interview Invitation"
       ? "We are pleased to invite you to an interview. Please use the scheduling link shared to pick a convenient slot."
       : type === "Feedback Request"
       ? "We would appreciate your feedback on your recent interview experience with our team."
       : type === "Assessment Notification"
       ? "As the next step, we would like you to complete a short online assessment within the next 48 hours."
       : type === "Offer Letter"
       ? "Congratulations! We are delighted to extend an offer for this role. Your formal offer letter is attached."
       : "Thank you for your interest. Our recruitment team will be in touch with the next steps shortly."
   }</p>
   <p>Warm regards,<br/>Talent Acquisition Team</p>`

const SHORT_MESSAGE = (person, type, job) => {
  switch (type) {
    case "Interview Invitation":
      return `Hi ${person.name.split(" ")[0]}, you're invited for an interview${
        job ? ` for ${job.jobTitle}` : ""
      }. Reply YES to confirm your slot.`
    case "Assessment Notification":
      return `Hi ${person.name.split(" ")[0]}, please complete your online assessment${
        job ? ` for ${job.jobTitle}` : ""
      } within 48 hrs. Link sent to your email.`
    case "Screening Reminder":
      return `Reminder: your screening call${
        job ? ` for ${job.jobTitle}` : ""
      } is scheduled tomorrow at 11:00 AM. Reply STOP to opt out.`
    case "Document Request":
      return `Hi ${person.name.split(" ")[0]}, kindly share your updated resume and ID proof to proceed with your application.`
    default:
      return `Hi ${person.name.split(" ")[0]}, thank you for applying${
        job ? ` for ${job.jobTitle}` : ""
      }. We'll update you on the next steps soon.`
  }
}

/* Deterministic pseudo-random so the demo data is stable across renders. */
let SEED = 987654321
const rand = () => {
  SEED = (SEED * 1103515245 + 12345) & 0x7fffffff
  return SEED / 0x7fffffff
}
const pick = arr => arr[Math.floor(rand() * arr.length)]

const buildDataset = () => {
  const base = new Date()
  base.setHours(9, 15, 0, 0)
  const rows = []
  let id = 1

  const makeStatus = channel => {
    const r = rand()
    if (channel === "email") {
      if (r < 0.12) return { status: "Pending", deliveredAt: null, readAt: null, failure: null }
      if (r < 0.28) return { status: "Failed", deliveredAt: null, readAt: null, failure: pick(["Invalid recipient address", "Mailbox full", "SMTP relay timeout", "Blocked by recipient spam filter"]) }
      if (r < 0.6) return { status: "Sent", deliveredAt: null, readAt: null, failure: null }
      // Delivered only when provider confirms
      return { status: "Delivered", deliveredAt: true, readAt: null, failure: null }
    }
    if (channel === "sms") {
      if (r < 0.14) return { status: "Pending", deliveredAt: null, readAt: null, failure: null }
      if (r < 0.3) return { status: "Failed", deliveredAt: null, readAt: null, failure: pick(["Invalid mobile number", "DND / carrier blocked", "Message expired", "Gateway rejected"]) }
      if (r < 0.62) return { status: "Sent", deliveredAt: null, readAt: null, failure: null }
      return { status: "Delivered", deliveredAt: true, readAt: null, failure: null }
    }
    // whatsapp
    if (r < 0.12) return { status: "Pending", deliveredAt: null, readAt: null, failure: null }
    if (r < 0.24) return { status: "Failed", deliveredAt: null, readAt: null, failure: pick(["Recipient not on WhatsApp", "24-hour window expired", "Template not approved", "Number opted out"]) }
    if (r < 0.46) return { status: "Sent", deliveredAt: null, readAt: null, failure: null }
    if (r < 0.72) return { status: "Delivered", deliveredAt: true, readAt: null, failure: null }
    return { status: "Read", deliveredAt: true, readAt: true, failure: null } // Read only when provider confirms
  }

  const channels = ["email", "sms", "whatsapp"]
  channels.forEach(channel => {
    const count = channel === "email" ? 26 : channel === "sms" ? 20 : 18
    for (let i = 0; i < count; i++) {
      const person = pick(PEOPLE)
      const sender = pick(SENDERS)
      const type = pick(NOTIFICATION_TYPES)
      // ~1 in 6 records has no associated job (must remain visible under "All Jobs")
      const job = rand() < 0.16 ? null : pick(JOBS)

      const sentAt = new Date(base.getTime() - Math.floor(rand() * 30 * 24 * 60) * 60 * 1000)
      const st = makeStatus(channel)
      const deliveredAt = st.deliveredAt
        ? new Date(sentAt.getTime() + Math.floor(2 + rand() * 55) * 60 * 1000)
        : null
      const readAt = st.readAt
        ? new Date((deliveredAt || sentAt).getTime() + Math.floor(3 + rand() * 120) * 60 * 1000)
        : null

      const subject =
        channel === "email"
          ? `${type}${job ? ` — ${job.jobTitle}` : ""}`
          : channel === "whatsapp"
          ? type
          : null

      const body =
        channel === "email"
          ? EMAIL_BODY(person, type, job)
          : SHORT_MESSAGE(person, type, job)

      const attachments =
        channel === "email" && (type === "Offer Letter" || rand() < 0.25)
          ? [
              { name: type === "Offer Letter" ? "Offer_Letter.pdf" : "Job_Description.pdf", size: "148 KB" },
              ...(rand() < 0.4 ? [{ name: "Assessment_Guidelines.pdf", size: "92 KB" }] : []),
            ]
          : []

      rows.push({
        id: `${channel.toUpperCase()}-${1000 + id++}`,
        channel,
        sentAt: sentAt.toISOString(),
        recipientName: person.name,
        recipientEmail: person.email,
        recipientMobile: person.mobile,
        jobId: job ? job.jobId : null,
        jobTitle: job ? job.jobTitle : null,
        notificationType: type,
        subject,
        body,
        preview: truncate(channel === "email" ? subject : body, 70),
        cc: channel === "email" && rand() < 0.3 ? "recruiter.lead@company.com" : "",
        bcc: channel === "email" && rand() < 0.2 ? "audit@company.com" : "",
        attachments,
        fromAddress:
          channel === "email"
            ? "talent@company.com"
            : channel === "sms"
            ? "VD-ATSHR"
            : "+91 80000 12345",
        sentBy: sender,
        status: st.status,
        deliveredAt: deliveredAt ? deliveredAt.toISOString() : null,
        readAt: readAt ? readAt.toISOString() : null,
        failureReason: st.failure,
      })
    }
  })

  return rows
}

/* ------------------------------------------------------------------ */
/*  Details side panel                                                 */
/* ------------------------------------------------------------------ */
const DetailRow = ({ label, children }) => (
  <div style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: "1px solid #eef2f7" }}>
    <div style={{ width: 130, flexShrink: 0, fontSize: 12, fontWeight: 700, color: "#64748b" }}>
      {label}
    </div>
    <div style={{ fontSize: 13, color: "#1f2f46", wordBreak: "break-word", flex: 1 }}>
      {children || "-"}
    </div>
  </div>
)

const DetailsPanel = ({ record, channelCfg, onClose }) => {
  if (!record) return null
  const cfg = channelCfg
  return (
    <>
      {/* Keep raw HTML email bodies inside the panel — no horizontal scroll */}
      <style>{`
        .comm-email-body { word-break: break-word; overflow-wrap: anywhere; }
        .comm-email-body * { max-width: 100% !important; box-sizing: border-box; }
        .comm-email-body img,
        .comm-email-body video { max-width: 100% !important; height: auto !important; }
        .comm-email-body table { max-width: 100% !important; width: auto !important; table-layout: auto !important; }
        .comm-email-body td,
        .comm-email-body th { word-break: break-word; overflow-wrap: anywhere; white-space: normal !important; }
        .comm-email-body p,
        .comm-email-body div,
        .comm-email-body span { max-width: 100% !important; }
        .comm-email-body::-webkit-scrollbar { width: 8px; }
        .comm-email-body::-webkit-scrollbar-thumb { background: #cdd8e3; border-radius: 8px; }
        .comm-email-body::-webkit-scrollbar-track { background: transparent; }
        .comm-panel-body::-webkit-scrollbar { width: 9px; }
        .comm-panel-body::-webkit-scrollbar-thumb { background: #cdd8e3; border-radius: 9px; }
        .comm-panel-body::-webkit-scrollbar-track { background: transparent; }
        @keyframes commModalIn {
          from { opacity: 0; transform: translate(-50%, -46%) scale(0.98); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.45)",
          zIndex: 1050,
          animation: "commFade 0.15s ease",
        }}
      />
      {/* Panel */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          height: "min(90vh, 900px)",
          width: "min(860px, calc(100% - 32px))",
          transform: "translate(-50%, -50%)",
          borderRadius: 12,
          background: "#fff",
          zIndex: 1051,
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "commModalIn 0.22s ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            background: "linear-gradient(135deg, #12395c 0%, #0f766e 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "rgba(255,255,255,0.16)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                border: "1px solid rgba(255,255,255,0.22)",
              }}
            >
              {cfg.icon}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{cfg.short} Details</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
                {record.id} · {record.notificationType}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "rgba(255,255,255,0.16)",
              border: "none",
              color: "#fff",
              width: 32,
              height: 32,
              borderRadius: 8,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="comm-panel-body" style={{ padding: "12px 24px 28px", overflowY: "auto", flex: 1 }}>
          <div style={{ margin: "12px 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
            <StatusBadge status={record.status} />
            {record.status === "Failed" && (
              <span style={{ fontSize: 12, color: "#b42318", fontWeight: 600 }}>
                <FaExclamationTriangle style={{ marginBottom: 2, marginRight: 4 }} />
                Delivery failed
              </span>
            )}
          </div>

          <div style={{ marginTop: 10, fontSize: 11, fontWeight: 800, color: "#0b3a5d", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Recipient & Job
          </div>
          <DetailRow label="Recipient">{record.recipientName}</DetailRow>
          <DetailRow label={cfg.contactLabel}>
            {cfg.key === "email" ? record.recipientEmail : record.recipientMobile}
          </DetailRow>
          <DetailRow label="Associated Job">
            {record.jobTitle ? (
              <>
                {record.jobTitle}{" "}
                <span style={{ color: "#64748b" }}>(Job ID: {record.jobId})</span>
              </>
            ) : (
              <span style={{ color: "#94a3b8" }}>Not linked to a job</span>
            )}
          </DetailRow>
          <DetailRow label="Notification">{record.notificationType}</DetailRow>
          <DetailRow label="Channel">{cfg.short}</DetailRow>
          <DetailRow label="Sent By">{record.sentBy}</DetailRow>

          <div style={{ marginTop: 16, fontSize: 11, fontWeight: 800, color: "#0b3a5d", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {cfg.key === "email" ? "Message" : "Message Content"}
          </div>
          {cfg.key !== "sms" && <DetailRow label={cfg.key === "email" ? "Subject" : "Header"}>{record.subject}</DetailRow>}
          <DetailRow label="From">{record.fromAddress}</DetailRow>
          {cfg.key === "email" && <DetailRow label="CC">{record.cc}</DetailRow>}
          {cfg.key === "email" && <DetailRow label="BCC">{record.bcc}</DetailRow>}

          <div style={{ padding: "12px 0" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>
              {cfg.key === "email" ? "Email Body" : "Full Message"}
            </div>
            <div
              className="comm-email-body"
              style={{
                border: "1px solid #e5edf6",
                borderRadius: 8,
                padding: "12px 14px",
                background: "#fff",
                fontSize: 13,
                lineHeight: 1.6,
                color: "#1f2f46",
                overflowX: "hidden",
              }}
              dangerouslySetInnerHTML={{ __html: record.body }}
            />
          </div>

          {cfg.key === "email" && record.attachments.length > 0 && (
            <div style={{ padding: "4px 0 12px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>
                Attachments ({record.attachments.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {record.attachments.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      border: "1px solid #e5edf6",
                      borderRadius: 8,
                      padding: "8px 12px",
                      fontSize: 13,
                      background: "#fff",
                    }}
                  >
                    <FaPaperclip style={{ color: "#2563eb" }} />
                    <span style={{ flex: 1, color: "#1f2f46" }}>{a.name}</span>
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>{a.size}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 8, fontSize: 11, fontWeight: 800, color: "#0b3a5d", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Timeline
          </div>
          <DetailRow label="Sent">
            {formatDateTime(record.sentAt)} <span style={{ color: "#94a3b8" }}>({TZ_LABEL})</span>
          </DetailRow>
          {record.deliveredAt && (
            <DetailRow label="Delivered">
              {formatDateTime(record.deliveredAt)} <span style={{ color: "#94a3b8" }}>({TZ_LABEL})</span>
            </DetailRow>
          )}
          {record.readAt && (
            <DetailRow label="Read">
              {formatDateTime(record.readAt)} <span style={{ color: "#94a3b8" }}>({TZ_LABEL})</span>
            </DetailRow>
          )}
          {record.failureReason && (
            <DetailRow label="Failure Reason">
              <span style={{ color: "#b42318" }}>{record.failureReason}</span>
            </DetailRow>
          )}
        </div>
      </div>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
const EMPTY_APPLIED = { job: null, fromDate: "", toDate: "", status: null, search: "" }

const CommunicationHistory = () => {
  document.title = "ATS - Communication History"

  const [rows, setRows] = useState([]) // current page of records (server-driven)
  const [tabCounts, setTabCounts] = useState({ email: 0, sms: 0, whatsapp: 0 })
  const [totalRows, setTotalRows] = useState(0)
  const [jobsList, setJobsList] = useState([])
  const [jobPage, setJobPage] = useState(1)
  const [jobTotal, setJobTotal] = useState(0)
  const [jobSearch, setJobSearch] = useState("")
  const [loadingJobs, setLoadingJobs] = useState(false)
  const jobSearchInputRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [usingDemo, setUsingDemo] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [activeChannel, setActiveChannel] = useState("email")
  const [selectedRecord, setSelectedRecord] = useState(null)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  // Draft filters = what's in the form; applied = what actually filters the table.
  const [draft, setDraft] = useState({ ...EMPTY_APPLIED })
  const [applied, setApplied] = useState({ ...EMPTY_APPLIED })
  const [dateError, setDateError] = useState("")

  const cfg = CHANNELS[activeChannel]

  /* Company scope, mirroring the other ATS report pages. The selected company
     drives which records (and which jobs) the page shows. */
  const primaryCompanyId = useMemo(() => {
    try {
      const info = JSON.parse(sessionStorage.getItem("user-info"))
      return info?.user_details?.companies_data?.[0]?.company_id || null
    } catch (e) {
      return null
    }
  }, [])
  const [companyId, setCompanyId] = useState(primaryCompanyId)
  const [companyOptions, setCompanyOptions] = useState([])

  /* Load the companies this user is allowed to see (for the filter dropdown). */
  useEffect(() => {
    if (!primaryCompanyId) return
    let cancelled = false
    axios
      .get(`api/v1/companyv1?company_id=${primaryCompanyId}`)
      .then(res => {
        if (cancelled) return
        const list = res.data?.message || []
        setCompanyOptions(
          list
            .filter(c => c && c.company_id)
            .map(c => ({ value: c.company_id, label: c.company_domain || `Company #${c.company_id}` }))
        )
      })
      .catch(() => {
        /* Fallback: at least offer the user's own companies from the session. */
        try {
          const info = JSON.parse(sessionStorage.getItem("user-info"))
          const own = info?.user_details?.companies_data || []
          setCompanyOptions(
            own
              .filter(c => c && c.company_id)
              .map(c => ({ value: c.company_id, label: c.company_domain || c.company_name || `Company #${c.company_id}` }))
          )
        } catch (e) {
          setCompanyOptions([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [primaryCompanyId])

  /* Changing company refetches and resets the job filter (jobs are per-company). */
  const handleCompanyChange = opt => {
    const newId = opt ? opt.value : null
    setCompanyId(newId)
    setPage(1)
    setDraft(d => ({ ...d, job: null }))
    setApplied(a => ({ ...a, job: null }))
    setJobPage(1)
    setJobSearch("")
  }

  const fetchJobOptions = useCallback(async (pageNum = 1, search = "") => {
    if (!companyId) {
      setJobsList([])
      setJobTotal(0)
      return
    }
    setLoadingJobs(true)
    try {
      const response = await axios.get("/api/v1/create-job-order/", {
        params: { company_id: companyId, page: pageNum, size: 10, search: search.trim() || undefined },
      })
      const data = response.data || {}
      const results = data.results || data.data || data.jobs || (Array.isArray(data) ? data : [])
      setJobsList(Array.isArray(results) ? results : [])
      setJobTotal(Number(data.count ?? data.total ?? data.total_count ?? results.length) || 0)
      setJobPage(pageNum)
    } catch (err) {
      setJobsList([])
      setJobTotal(0)
    } finally {
      setLoadingJobs(false)
    }
  }, [companyId])

  useEffect(() => {
    fetchJobOptions(1, "")
  }, [fetchJobOptions])

  /* ------------------------------------------------------------------ */
  /*  Server-driven fetch of the live aggregate endpoint.                */
  /*  Falls back to realistic demo data if the endpoint is unreachable,  */
  /*  so the page always stays reviewable.                               */
  /* ------------------------------------------------------------------ */
  const applyDemoData = useCallback(
    (channel, filters, pageNum, size) => {
      const dataset = buildDataset()
      const match = (r, ch, statusAware) => {
        if (r.channel !== ch) return false
        if (filters.job && filters.job.value !== "all" && r.jobId !== filters.job.value) return false
        if (filters.fromDate && new Date(r.sentAt) < new Date(filters.fromDate + "T00:00:00")) return false
        if (filters.toDate && new Date(r.sentAt) > new Date(filters.toDate + "T23:59:59")) return false
        if (statusAware && filters.status && filters.status.value !== "all" && r.status !== filters.status.value) return false
        if (filters.search && filters.search.trim()) {
          const q = filters.search.trim().toLowerCase()
          const hay = `${r.recipientName} ${r.recipientEmail} ${r.recipientMobile}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      }
      const counts = {
        email: dataset.filter(r => match(r, "email", false)).length,
        sms: dataset.filter(r => match(r, "sms", false)).length,
        whatsapp: dataset.filter(r => match(r, "whatsapp", false)).length,
      }
      const all = dataset
        .filter(r => match(r, channel, true))
        .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt))
      const jobsMap = new Map()
      dataset.forEach(r => {
        if (r.jobId && !jobsMap.has(r.jobId)) jobsMap.set(r.jobId, r.jobTitle)
      })
      setJobsList(Array.from(jobsMap, ([jobId, jobTitle]) => ({ jobId, jobTitle })))
      setTabCounts(counts)
      setTotalRows(all.length)
      setRows(all.slice((pageNum - 1) * size, pageNum * size))
      setUsingDemo(true)
    },
    []
  )

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(false)
    const source = axios.CancelToken.source()

    const params = new URLSearchParams()
    params.append("channel", activeChannel)
    params.append("page", page)
    params.append("size", perPage)
    if (companyId) params.append("company_id", companyId)
    if (applied.job && applied.job.value !== "all") params.append("job_id", applied.job.value)
    if (applied.fromDate) params.append("from_date", applied.fromDate)
    if (applied.toDate) params.append("to_date", applied.toDate)
    if (applied.status && applied.status.value !== "all") params.append("status", applied.status.value)
    if (applied.search && applied.search.trim()) params.append("search", applied.search.trim())

    axios
      .get(`api/v1/communication-history/?${params.toString()}`, { cancelToken: source.token })
      .then(res => {
        const d = res.data || {}
        setRows(Array.isArray(d.results) ? d.results : [])
        setTabCounts(d.counts || { email: 0, sms: 0, whatsapp: 0 })
        setTotalRows(d.total || 0)
        setUsingDemo(false)
        setLoading(false)
      })
      .catch(err => {
        if (axios.isCancel(err)) return
        // Endpoint unavailable → gracefully fall back to demo data.
        console.warn("Communication history API unavailable, using demo data:", err?.message)
        applyDemoData(activeChannel, applied, page, perPage)
        setLoading(false)
      })

    return () => source.cancel("component-unmounted-or-refetch")
  }, [activeChannel, applied, page, perPage, companyId, applyDemoData])

  useEffect(() => {
    const cleanup = fetchData()
    return cleanup
  }, [fetchData])

  /* -------- Job options for the searchable dropdown -------- */
  const jobOptions = useMemo(() => {
    const opts = jobsList
      .map(j => ({
        value: j?.jobId ?? j?.job_id ?? j?.id ?? j?.job_order_id,
        label: `${j?.jobTitle ?? j?.job_title ?? j?.position ?? "Job Order"} (#${j?.jobId ?? j?.job_id ?? j?.id ?? j?.job_order_id})`,
      }))
      .filter(j => j.value !== undefined && j.value !== null)
      .sort((a, b) => a.label.localeCompare(b.label))
    return [{ value: "all", label: "All Jobs" }, ...opts]
  }, [jobsList])

  const jobMenuList = props => (
    <selectComponents.MenuList {...props}>
      {props.children}
      <div
        onMouseDown={event => event.stopPropagation()}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderTop: "1px solid #dbe3ec", background: "#fff", color: "#64748b", fontSize: 12 }}
      >
        <button type="button" disabled={loadingJobs || jobPage <= 1} onClick={() => fetchJobOptions(jobPage - 1, jobSearch)} style={{ padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 5, background: "#fff", color: "#64748b" }}>‹ Prev</button>
        <span>Page {jobPage} / {Math.max(1, Math.ceil(jobTotal / 10))}</span>
        <button type="button" disabled={loadingJobs || jobPage >= Math.ceil(jobTotal / 10)} onClick={() => fetchJobOptions(jobPage + 1, jobSearch)} style={{ padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 5, background: "#fff", color: "#64748b" }}>Next ›</button>
      </div>
    </selectComponents.MenuList>
  )

  const statusOptions = useMemo(
    () => [
      { value: "all", label: "All Statuses" },
      ...cfg.statusOptions.map(s => ({ value: s, label: s })),
    ],
    [cfg]
  )

  /* Rows are already filtered + newest-first from the server. */
  const filteredRows = rows

  /* -------- Filter actions -------- */
  const validateDates = (from, to) => {
    if (from && to && new Date(from) > new Date(to)) {
      setDateError("From Date cannot be later than To Date.")
      return false
    }
    setDateError("")
    return true
  }

  const handleApply = () => {
    if (!validateDates(draft.fromDate, draft.toDate)) return
    setPage(1)
    setApplied({ ...draft })
  }

  const handleReset = () => {
    setDraft({ ...EMPTY_APPLIED })
    setApplied({ ...EMPTY_APPLIED })
    setDateError("")
    setPage(1)
  }

  /* -------- Export the current channel's filtered records to Excel -------- */
  const buildExportRows = records =>
    records.map(r => ({
      "Sent Date & Time": `${formatDateTime(r.sentAt)} (${TZ_LABEL})`,
      Channel: CHANNELS[r.channel] ? CHANNELS[r.channel].short : r.channel,
      "Recipient Name": r.recipientName || "-",
      "Email Address": r.recipientEmail || "-",
      "Mobile Number": r.recipientMobile || "-",
      "Job Title": r.jobTitle || "-",
      "Job ID": r.jobId || "-",
      "Notification Type": r.notificationType || "-",
      "Subject / Message": (r.channel === "email" ? r.subject : r.body || r.subject) || "-",
      "Sent By": r.sentBy || "-",
      Status: r.status || "-",
      "Delivered At": r.deliveredAt ? `${formatDateTime(r.deliveredAt)} (${TZ_LABEL})` : "-",
      "Read At": r.readAt ? `${formatDateTime(r.readAt)} (${TZ_LABEL})` : "-",
      "Failure Reason": r.failureReason || "-",
    }))

  const writeWorkbook = records => {
    if (!records.length) return
    import("xlsx")
      .then(xlsx => {
        const worksheet = xlsx.utils.json_to_sheet(buildExportRows(records))
        // Sensible column widths for readability.
        worksheet["!cols"] = [
          { wch: 24 }, { wch: 10 }, { wch: 22 }, { wch: 28 }, { wch: 18 },
          { wch: 28 }, { wch: 8 }, { wch: 22 }, { wch: 50 }, { wch: 18 },
          { wch: 12 }, { wch: 24 }, { wch: 24 }, { wch: 30 },
        ]
        const workbook = { Sheets: { [cfg.short]: worksheet }, SheetNames: [cfg.short] }
        const buffer = xlsx.write(workbook, { bookType: "xlsx", type: "array" })
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        })
        saveAs(blob, `communication_history_${cfg.key}_${new Date().getTime()}.xlsx`)
      })
      .catch(err => console.error("Excel export failed:", err))
      .finally(() => setExporting(false))
  }

  const handleExport = () => {
    if (exporting || loading) return
    setExporting(true)

    // Demo mode: export the filtered demo set directly.
    if (usingDemo) {
      const dataset = buildDataset()
      const match = r => {
        if (r.channel !== activeChannel) return false
        if (applied.job && applied.job.value !== "all" && r.jobId !== applied.job.value) return false
        if (applied.fromDate && new Date(r.sentAt) < new Date(applied.fromDate + "T00:00:00")) return false
        if (applied.toDate && new Date(r.sentAt) > new Date(applied.toDate + "T23:59:59")) return false
        if (applied.status && applied.status.value !== "all" && r.status !== applied.status.value) return false
        if (applied.search && applied.search.trim()) {
          const q = applied.search.trim().toLowerCase()
          const hay = `${r.recipientName} ${r.recipientEmail} ${r.recipientMobile}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      }
      const filtered = dataset
        .filter(match)
        .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt))
      writeWorkbook(filtered)
      return
    }

    // Live mode: fetch every filtered record for the active channel.
    const params = new URLSearchParams()
    params.append("channel", activeChannel)
    params.append("export", "true")
    if (companyId) params.append("company_id", companyId)
    if (applied.job && applied.job.value !== "all") params.append("job_id", applied.job.value)
    if (applied.fromDate) params.append("from_date", applied.fromDate)
    if (applied.toDate) params.append("to_date", applied.toDate)
    if (applied.status && applied.status.value !== "all") params.append("status", applied.status.value)
    if (applied.search && applied.search.trim()) params.append("search", applied.search.trim())

    axios
      .get(`api/v1/communication-history/?${params.toString()}`)
      .then(res => writeWorkbook(Array.isArray(res.data?.results) ? res.data.results : []))
      .catch(err => {
        console.error("Export fetch failed:", err?.message)
        setExporting(false)
      })
  }

  /* Switching tabs retains Job + Date filters; status resets (channel-specific). */
  const handleTabChange = channelKey => {
    if (channelKey === activeChannel) return
    setActiveChannel(channelKey)
    setSelectedRecord(null)
    setPage(1)
    setDraft(d => ({ ...d, status: null }))
    setApplied(a => ({ ...a, status: null }))
  }

  /* -------- Table columns -------- */
  const columns = useMemo(
    () => [
      {
        name: "Sent Date & Time",
        selector: row => row.sentAt,
        sortable: true,
        width: "165px",
        cell: row => (
          <div style={{ lineHeight: 1.35, textAlign: "center", width: "100%" }}>
            <div style={{ fontWeight: 600 }}>{formatDateTime(row.sentAt)}</div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>{TZ_LABEL}</div>
          </div>
        ),
      },
      {
        name: "Recipient",
        selector: row => row.recipientName,
        sortable: true,
        grow: 1.4,
        cell: row => (
          <div style={{ lineHeight: 1.35, minWidth: 0, textAlign: "center", width: "100%" }}>
            <div style={{ fontWeight: 700, color: "#102a43" }}>{row.recipientName}</div>
            <div
              title={cfg.key === "email" ? row.recipientEmail : row.recipientMobile}
              style={{
                fontSize: 11,
                color: "#64748b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 190,
                margin: "0 auto",
              }}
            >
              {cfg.key === "email" ? row.recipientEmail : row.recipientMobile}
            </div>
          </div>
        ),
      },
      {
        name: "Job",
        selector: row => row.jobTitle || "",
        grow: 1.3,
        cell: row =>
          row.jobTitle ? (
            <div style={{ lineHeight: 1.35, minWidth: 0, textAlign: "center", width: "100%" }}>
              <div
                title={row.jobTitle}
                style={{
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 170,
                  margin: "0 auto",
                }}
              >
                {row.jobTitle}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>#{row.jobId}</div>
            </div>
          ) : (
            <span style={{ fontSize: 11, color: "#b0bccd", fontStyle: "italic" }}>No job</span>
          ),
      },
      {
        name: "Type",
        selector: row => row.notificationType,
        grow: 1,
        cell: row => (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#334155",
              background: "#eef2f7",
              padding: "2px 8px",
              borderRadius: 6,
              whiteSpace: "nowrap",
            }}
          >
            {row.notificationType}
          </span>
        ),
      },
      {
        name: cfg.key === "email" ? "Subject" : "Message",
        selector: row => row.preview,
        grow: 1.7,
        cell: row => (
          <span
            title={cfg.key === "email" ? row.subject : row.body?.replace(/<[^>]*>/g, "")}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 240,
              color: "#334155",
              margin: "0 auto",
            }}
          >
            {cfg.key === "email" ? row.subject : truncate(row.body, 70)}
          </span>
        ),
      },
      {
        name: "Sent By",
        selector: row => row.sentBy,
        grow: 0.9,
        cell: row => (
          <span style={{ fontSize: 12, color: row.sentBy === "System" ? "#7c3aed" : "#334155", fontWeight: row.sentBy === "System" ? 700 : 500 }}>
            {row.sentBy}
          </span>
        ),
      },
      {
        name: "Status",
        selector: row => row.status,
        sortable: true,
        width: "120px",
        cell: row => <StatusBadge status={row.status} />,
      },
      {
        name: "Action",
        width: "110px",
        cell: row => (
          <button
            onClick={e => {
              e.stopPropagation()
              setSelectedRecord(row)
            }}
            style={{
              border: "1px solid #cfe0f1",
              background: "#f2f8ff",
              color: "#1d4ed8",
              fontSize: 11,
              fontWeight: 700,
              padding: "5px 10px",
              borderRadius: 6,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            View Details
          </button>
        ),
      },
    ],
    [cfg]
  )

  /* -------- Empty / no-results components for the table -------- */
  const NoData = () => {
    const hasActiveFilters =
      applied.job || applied.fromDate || applied.toDate || applied.status || applied.search
    return (
      <div style={{ padding: "48px 20px", textAlign: "center", width: "100%" }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: cfg.bg,
            color: cfg.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            marginBottom: 14,
          }}
        >
          {hasActiveFilters ? <FaSearch /> : <FaInbox />}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#334155" }}>
          {hasActiveFilters ? "No matching records" : `No ${cfg.short.toLowerCase()} history yet`}
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
          {hasActiveFilters
            ? "Try adjusting or resetting your filters to see more results."
            : `${cfg.short} communications will appear here once they are sent.`}
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleReset}
            style={{
              marginTop: 14,
              border: "1px solid #cfe0f1",
              background: "#fff",
              color: "#1d4ed8",
              fontSize: 12,
              fontWeight: 700,
              padding: "7px 16px",
              borderRadius: 7,
              cursor: "pointer",
            }}
          >
            <FaRedo style={{ marginRight: 6, marginBottom: 2 }} />
            Reset Filters
          </button>
        )}
      </div>
    )
  }

  const selectStyles = {
    control: base => ({
      ...base,
      minHeight: 38,
      borderColor: "#cfdbe8",
      boxShadow: "none",
      fontSize: 13,
      "&:hover": { borderColor: "#9db8d2" },
    }),
    menu: base => ({ ...base, zIndex: 20, fontSize: 13 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    valueContainer: base => ({ ...base, padding: "0 8px" }),
  }

  const channelTabs = (
    <div
      style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        justifyContent: "flex-end",
      }}
    >
      {Object.values(CHANNELS).map(channel => {
        const isActive = channel.key === activeChannel
        return (
          <button
            key={channel.key}
            onClick={() => handleTabChange(channel.key)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 11px",
              borderRadius: 9,
              border: isActive ? "1px solid rgba(255,255,255,0.85)" : "1px solid rgba(255,255,255,0.24)",
              background: isActive ? "#fff" : "rgba(255,255,255,0.12)",
              color: isActive ? "#102a43" : "#fff",
              boxShadow: isActive ? "0 8px 18px rgba(15,23,42,0.16)" : "none",
              cursor: "pointer",
              transition: "all 0.15s ease",
              minWidth: 145,
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: isActive ? channel.bg : "rgba(255,255,255,0.16)",
                color: isActive ? channel.accent : "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {channel.icon}
            </span>
            <span style={{ textAlign: "left", minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {channel.short}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: 10.5,
                  lineHeight: 1.2,
                  color: isActive ? "#64748b" : "rgba(255,255,255,0.72)",
                  whiteSpace: "nowrap",
                }}
              >
                {tabCounts[channel.key]} records
              </span>
            </span>
            <span
              style={{
                marginLeft: "auto",
                fontSize: 11,
                fontWeight: 800,
                color: isActive ? channel.accent : "#fff",
                background: isActive ? channel.bg : "rgba(255,255,255,0.16)",
                borderRadius: 999,
                padding: "2px 8px",
                minWidth: 28,
                textAlign: "center",
              }}
            >
              {tabCounts[channel.key]}
            </span>
          </button>
        )
      })}
    </div>
  )

  /* ---------------------------------------------------------------- */
  return (
    <div
      className="page-content"
      style={{
        background: "linear-gradient(180deg, #f3f7fb 0%, #eef4f8 48%, #f8fafc 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Panel animations */}
      <style>{`
        @keyframes commSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes commFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <Container fluid={true}>
        {/* ---------- Page header ---------- */}
        <div
          style={{
            borderRadius: 12,
            padding: "18px 22px",
            marginBottom: 16,
            background: "linear-gradient(135deg, #12395c 0%, #0f766e 100%)",
            color: "#fff",
            boxShadow: "0 12px 28px rgba(15, 57, 92, 0.18)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 280 }}>
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.16)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  border: "1px solid rgba(255,255,255,0.22)",
                  flexShrink: 0,
                }}
              >
                <FaInbox />
              </div>
              <div>
                <h1 className="mb-1" style={{ fontSize: 23, color: "#fff", fontWeight: 800 }}>
                  Communication History
                </h1>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 13 }}>
                  View all Email, SMS, and WhatsApp notifications sent from across the ATS in one place.
                  Times shown in your local timezone ({TZ_LABEL}).
                </div>
              </div>
            </div>
            {channelTabs}
          </div>
        </div>

        {/* ---------- Filter bar ---------- */}
        <div style={{ ...reportCardStyle, padding: "16px 16px 14px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <FaFilter style={{ color: "#0f766e" }} />
            <span style={{ fontWeight: 800, color: "#102a43", fontSize: 14 }}>Filters</span>
          </div>
          {/* Single-line filter row: everything (incl. buttons) sits inline
              and wraps gracefully only on very narrow screens. */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 150px", minWidth: 140 }}>
              <label style={labelStyle}>Company</label>
              <Select
                options={companyOptions}
                value={companyOptions.find(o => o.value === companyId) || null}
                onChange={handleCompanyChange}
                styles={selectStyles}
                isSearchable
                placeholder="Select company…"
                classNamePrefix="comm-select"
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              />
            </div>
            <div style={{ flex: "1 1 160px", minWidth: 150 }}>
              <label style={labelStyle}>Job</label>
              <Select
                inputRef={jobSearchInputRef}
                options={jobOptions}
                value={draft.job || jobOptions[0]}
                onChange={opt => setDraft(d => ({ ...d, job: opt }))}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    setJobSearch(value)
                    fetchJobOptions(1, value)
                  }
                  return value
                }}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    fetchJobOptions(1, jobSearch)
                    window.requestAnimationFrame(() => jobSearchInputRef.current?.focus())
                  }
                }}
                styles={selectStyles}
                isSearchable
                isLoading={loadingJobs}
                placeholder="Search job…"
                classNamePrefix="comm-select"
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                components={{ MenuList: jobMenuList }}
              />
            </div>
            <div style={{ flex: "0 1 140px", minWidth: 130 }}>
              <label style={labelStyle}>From Date</label>
              <input
                type="date"
                value={draft.fromDate}
                max={draft.toDate || undefined}
                onChange={e => {
                  const v = e.target.value
                  setDraft(d => ({ ...d, fromDate: v }))
                  validateDates(v, draft.toDate)
                }}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: "0 1 140px", minWidth: 130 }}>
              <label style={labelStyle}>To Date</label>
              <input
                type="date"
                value={draft.toDate}
                min={draft.fromDate || undefined}
                onChange={e => {
                  const v = e.target.value
                  setDraft(d => ({ ...d, toDate: v }))
                  validateDates(draft.fromDate, v)
                }}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: "1 1 140px", minWidth: 130 }}>
              <label style={labelStyle}>Status</label>
              <Select
                options={statusOptions}
                value={draft.status || statusOptions[0]}
                onChange={opt => setDraft(d => ({ ...d, status: opt }))}
                styles={selectStyles}
                placeholder="All Statuses"
                classNamePrefix="comm-select"
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              />
            </div>
            <div style={{ flex: "1 1 170px", minWidth: 150 }}>
              <label style={labelStyle}>Search</label>
              <div style={{ position: "relative" }}>
                <FaSearch
                  style={{
                    position: "absolute",
                    left: 11,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    fontSize: 12,
                  }}
                />
                <input
                  type="text"
                  value={draft.search}
                  onChange={e => setDraft(d => ({ ...d, search: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && handleApply()}
                  placeholder="Name, email or mobile…"
                  style={{ ...inputStyle, paddingLeft: 30 }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
              <button onClick={handleReset} style={secondaryBtn} title="Reset filters">
                <FaRedo style={{ marginRight: 6, marginBottom: 2 }} />
                Reset
              </button>
              <button
                onClick={handleApply}
                disabled={!!dateError}
                style={{ ...primaryBtn, opacity: dateError ? 0.6 : 1 }}
                title="Apply filters"
              >
                <FaFilter style={{ marginRight: 6, marginBottom: 2 }} />
                Apply
              </button>
            </div>
          </div>

          {dateError && (
            <div style={{ marginTop: 8 }}>
              <span style={{ color: "#b42318", fontSize: 12, fontWeight: 600 }}>
                <FaExclamationTriangle style={{ marginRight: 6, marginBottom: 2 }} />
                {dateError}
              </span>
            </div>
          )}
        </div>

        {/* ---------- Table ---------- */}
        <div style={{ ...reportCardStyle, overflow: "hidden" }}>
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #e5edf6",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              background: "#fff",
            }}
          >
            <div style={{ fontWeight: 800, color: "#102a43", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: cfg.accent }}>{cfg.icon}</span>
              {cfg.label}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {usingDemo && !loading && (
                <span
                  title="The live API was unreachable — showing sample data so the page stays reviewable."
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#92600a",
                    background: "#fef3d7",
                    padding: "2px 9px",
                    borderRadius: 999,
                  }}
                >
                  Demo data
                </span>
              )}
              <span style={{ color: "#64748b", fontSize: 12 }}>
                {loading ? "Loading…" : `${totalRows} record${totalRows === 1 ? "" : "s"}`}
              </span>
              <button
                onClick={handleExport}
                disabled={loading || exporting || totalRows === 0}
                title={totalRows === 0 ? "No records to export" : "Export the filtered records to Excel"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  border: "1px solid #cfe0d6",
                  background: loading || exporting || totalRows === 0 ? "#f1f5f4" : "#eaf7f0",
                  color: "#0f766e",
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "6px 13px",
                  borderRadius: 7,
                  cursor: loading || exporting || totalRows === 0 ? "not-allowed" : "pointer",
                  opacity: loading || exporting || totalRows === 0 ? 0.6 : 1,
                }}
              >
                <FaFileExcel />
                {exporting ? "Exporting…" : "Export to Excel"}
              </button>
            </div>
          </div>

          {error ? (
            <div style={{ padding: "48px 20px", textAlign: "center" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#fde4e1",
                  color: "#e5473b",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  marginBottom: 14,
                }}
              >
                <FaExclamationTriangle />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#334155" }}>
                Unable to load communication history
              </div>
              <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
                Something went wrong while fetching records. Please try again.
              </div>
              <button onClick={fetchData} style={{ ...primaryBtn, marginTop: 16 }}>
                <FaRedo style={{ marginRight: 6, marginBottom: 2 }} />
                Retry
              </button>
            </div>
          ) : loading ? (
            <div style={{ padding: 16 }}>
              <OrgTableShimmer columns={7} rows={8} titleWidth="240px" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredRows}
              customStyles={reportTableStyles}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={page}
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationComponentOptions={paginationComponentOptions}
              onChangePage={newPage => setPage(newPage)}
              onChangeRowsPerPage={newPerPage => {
                setPerPage(newPerPage)
                setPage(1)
              }}
              highlightOnHover
              pointerOnHover
              striped
              responsive
              persistTableHead
              noDataComponent={<NoData />}
              onRowClicked={row => setSelectedRecord(row)}
            />
          )}
        </div>
      </Container>

      {/* ---------- Details side panel ---------- */}
      <DetailsPanel
        record={selectedRecord}
        channelCfg={selectedRecord ? CHANNELS[selectedRecord.channel] : cfg}
        onClose={() => setSelectedRecord(null)}
      />
    </div>
  )
}

/* -------- Small shared inline styles -------- */
const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: "#475569",
  marginBottom: 5,
}
const inputStyle = {
  width: "100%",
  height: 38,
  border: "1px solid #cfdbe8",
  borderRadius: 6,
  padding: "0 10px",
  fontSize: 13,
  color: "#1f2f46",
  outline: "none",
  background: "#fff",
}
const primaryBtn = {
  border: "none",
  background: "linear-gradient(135deg, #12395c 0%, #0f766e 100%)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  padding: "9px 18px",
  borderRadius: 8,
  cursor: "pointer",
}
const secondaryBtn = {
  border: "1px solid #cfdbe8",
  background: "#fff",
  color: "#475569",
  fontSize: 13,
  fontWeight: 700,
  padding: "9px 18px",
  borderRadius: 8,
  cursor: "pointer",
}

export default CommunicationHistory
